import { useState } from 'react';
import type { Expense } from '@/types';
import { mockClients, mockProjects } from '@/mocks/data';

interface ExpensesListProps {
  expenses: Expense[];
  showActions?: boolean;
}

export function ExpensesList({ expenses, showActions = true }: ExpensesListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);

  const handleEdit = (id: string) => {
    setEditingId(id);
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this expense?')) {
      console.log('Delete expense:', id);
    }
  };

  const handleSave = (id: string) => {
    console.log('Save expense:', id);
    setEditingId(null);
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      Software: 'bg-blue-100 text-blue-700',
      Hardware: 'bg-gray-100 text-gray-700',
      Office: 'bg-green-100 text-green-700',
      Travel: 'bg-purple-100 text-purple-700',
      Marketing: 'bg-pink-100 text-pink-700',
      Education: 'bg-yellow-100 text-yellow-700',
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-700';
  };

  if (expenses.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No expenses found</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {expenses.map((expense) => {
        const client = mockClients.find((c) => c.id === expense.clientId);
        const project = mockProjects.find((p) => p.id === expense.projectId);
        const isEditing = editingId === expense.id;

        return (
          <div
            key={expense.id}
            className="p-4 rounded-lg hover:bg-white/30 transition-colors border border-white/20"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                {isEditing ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        defaultValue={expense.amount}
                        className="px-3 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                        placeholder="Amount"
                        min="0"
                        step="0.01"
                      />
                      <input
                        type="text"
                        defaultValue={expense.category}
                        className="px-3 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                        placeholder="Category"
                      />
                    </div>
                    <input
                      type="text"
                      defaultValue={expense.notes || ''}
                      className="w-full px-3 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                      placeholder="Notes"
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${getCategoryColor(expense.category)}`}
                      >
                        {expense.category}
                      </span>
                      {expense.isRecurring && (
                        <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700">
                          Recurring
                        </span>
                      )}
                    </div>

                    {expense.notes && <p className="text-sm text-gray-700 mb-2">{expense.notes}</p>}

                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>
                        {new Date(expense.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                      {client && (
                        <>
                          <span>•</span>
                          <span>{client.name}</span>
                        </>
                      )}
                      {project && (
                        <>
                          <span>•</span>
                          <span>{project.name}</span>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="ml-4 flex items-center gap-2">
                {isEditing ? (
                  <>
                    <button
                      onClick={() => handleSave(expense.id)}
                      className="p-1 text-green-600 hover:bg-green-100 rounded"
                      title="Save"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={handleCancel}
                      className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                      title="Cancel"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-red-600 mr-2">
                      €{expense.amount.toFixed(2)}
                    </span>
                    {showActions && (
                      <>
                        <button
                          onClick={() => handleEdit(expense.id)}
                          className="p-1 text-gray-600 hover:bg-white/50 rounded"
                          title="Edit"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          title="Delete"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
